import {
    Accordion,
    AccordionProps,
    Breakpoints,
    CTA,
    Text,
    TextTag,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import {ArrowRight, Info} from '@volkswagen-onehub/icons-core'
import {LayerManagerV2} from '@volkswagen-onehub/layer-manager'
import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'
import styled from 'styled-components'
import {gridUtils} from '../../../assets/css-vars/gridUtils'
import {AccordionSettings} from '../../../common/configuration'
import {
    setIsLegalNotesOpen,
    setLegalNotesText,
    setSelectedPromotion,
} from '../../../features/general/generalSlice'
import TrackingAction from '../../../tracking-manager'
import {
    AdditionalPromoText,
    Promotion,
    SingleEquipment,
    SinglePromotionEquipment,
} from '../../../types/singleModelInterface'
import {useFeatureServices} from '../../../use-feature-services'
import {getPromoTitle} from '../../../utils/promos/getPromoTitle'
import {hasLegalNotes} from '../../../utils/promos/hasLegalNotes'
import {isExpandablePromotion} from '../../../utils/promos/isExpandablePromotion'
import {
    getAccordionCloseClickData,
    getAccordionOpenClickData,
    getVehiclePromotionsButtonClickData,
    getVehiclePromotionsLinkClickData,
} from '../../../utils/tracking/getTrackingData'
import setTracking, {
    checkTrackingManager,
} from '../../../utils/tracking/tracking-utils/setTracking'
import {RootState} from '../../rootReducer'
import {AppDispatch} from '../../thunk'
import legalNotesLayerHandle from '../layers/handles/legalNotesLayerHandle'
import EyeCatcher from './EyeCatcher'
import PromotionalPackage from './PromotionalPackage'
import PromotionPrice, {hasPromotionPrice} from './PromotionPrice'
import StandardEquipment from './StandardEquipment'
import DealerHighlighter from './DealerHighlighter'
import FuelTags from './FuelTags'
import {Global, PromotionType} from '../../../shared/variables'
import {capitalizeWords} from '../../../utils/text/dealer'
import STYlE_CONSTANTS from '../../../utils/style/styleConstants'
import {openCouponLayer} from '../layers/launchers/CouponRequestLayerHandle'
import {setFormCycleID} from '../../../features/userData/userDataSlice'
import {generateRandomString} from '../../../utils/tracking/tracking-utils/generateRandomString'
import {anchors} from '../../../assets/css-vars/anchors'
import {globalColors} from '../../../utils/style/globalColors'
import {
    PURPLE_EYE_CATCHER_BACKGROUND,
    ULTRAMARINE_EYE_CATCHER_BACKGROUND,
} from '../../../utils/style/eyeCatcherColors'

interface AccordionItemWrapperProps {
    removeArrow: boolean
}

const PROMO_AVAILABLE_CLASS = 'IsPromoAvailable'

const CTAContainer = styled.div`
    margin-top: 15px;

    button {
        display: inline-block;

        > * {
            pointer-events: none;
        }

        span {
            display: flex;
            align-items: center;
        }

        svg {
            width: 22px;
            height: 22px;
            display: inline-block;
            margin-right: 10px;
        }
    }
`

const AccordionInner = styled.div`
    ${anchors}

    @media only screen and (min-width: ${Breakpoints.b560}px) {
        padding-right: ${gridUtils.gapDouble};
        padding-left: ${gridUtils.gapDouble};
    }
`

const SubTitleContainer = styled.div`
    margin-top: 15px;

    @media (min-width: ${Breakpoints.b560}px) {
        margin-top: 20px;
    }

    p {
        margin-bottom: 0;
    }
`

const EyeCatcherAccordionContainer = styled.div`
    margin-top: 15px;
`

const EstimateCtaContainer = styled.div`
    margin-top: 44px;
    margin-bottom: 20px;
`

const PromotionalPackageContainer = styled.div`
    margin-top: 50px;
    margin-bottom: 95px;

    @media only screen and (min-width: ${Breakpoints.b960}px) {
        margin-top: 70px;
        margin-bottom: 80px;
    }
`

const StandardEquipmentContainer = styled.div`
    margin-top: 48px;
    margin-bottom: 50px;

    @media only screen and (min-width: ${Breakpoints.b960}px) {
        margin-top: 56px;
        margin-bottom: 70px;
    }
`

const HeadlineTitle = styled(Text)`
    p {
        display: inline;
        margin: 0;
    }

    span {
        word-break: break-word;
    }
`

const HeadlineTitleText = styled.span`
    margin-right: 10px;
`

const HeadlineFuelTags = styled(FuelTags)`
    margin-bottom: ${(props) => props.theme.size.static100};
`

const HeadlineSubTitle = styled(Text)`
    font-weight: ${STYlE_CONSTANTS.TEXT_WEIGHT_LIGHT};
    font-size: 14px;
    line-height: 1.2;
    margin: 0 0 5px 0;

    @media (min-width: ${Breakpoints.b560}px) {
        font-size: 18px;
    }
`
const AccordionItemWrapper = styled.div<AccordionItemWrapperProps>`
    cursor: initial;
    scroll-margin-top: 50px;
    position: relative;

    @media (min-width: ${Breakpoints.b960}px) {
        scroll-margin-top: 100px;
    }

    button:hover:not(:disabled) *,
    button:focus-within:not(:disabled) * {
        color: inherit;

        div {
            color: ${(props) => props.theme.colors.content.hover};
        }

        .EyecatcherPurple {
            color: ${(props) => props.theme.colors.content.onsurface};
        }
    }

    &.${PROMO_AVAILABLE_CLASS} {
        margin-top: ${(props) => props.theme.size.dynamic0100};
    }

    ${(props) =>
        props.removeArrow
            ? '[class^="StyledIcon"] { display: none !important; }'
            : ''}
`
const AccordionSubText = styled(Text)`
    display: inline-block;
    line-height: 1.2;

    @media (min-width: ${Breakpoints.b560}px) {
        margin-right: 5px;
    }

    @media (min-width: ${Breakpoints.b960}px) {
        font-size: 16px;
    }
`

const SimpleItemCTA = styled.div`
    display: block;

    @media (min-width: ${Breakpoints.b560}px) {
        display: inline-block;
        font-size: 16px;
    }

    a {
        font-size: 14px;
        line-height: 1.2;

        @media (min-width: ${Breakpoints.b560}px) {
            font-size: 16px;
        }

        svg {
            margin-left: 5px;
        }

        span {
            display: inline-flex;
            align-items: center;
        }
    }
`

const HeadlinePriceContainer = styled(Text)`
    font-weight: ${STYlE_CONSTANTS.TEXT_WEIGHT_LIGHT};
`

const LegalNotesCta = styled(CTA)`
    font-size: 14px;

    @media (min-width: ${Breakpoints.b560}px) {
        font-size: 16px;
    }
`

const FakeAccordion = styled.div`
    padding: ${(props) => props.theme.size.static350} 0
        ${(props) => props.theme.size.static400};
    border-bottom: ${(props) => props.theme.size.border.default} solid
        ${(props) => props.theme.colors.border.divider};
`

const getCtaLabel = (
    ctaText: string,
    title: string,
    isPromoService: boolean,
    customizableCtaText?: string | null
): string =>
    isPromoService && customizableCtaText
        ? customizableCtaText
        : `${ctaText} su ${title}`

export interface AccordionItem extends Promotion {
    isOpen: boolean
    openIndex: number
    onClick: (accordionIndex: number, updateUrlSlug: boolean) => void
    onClose?: () => void
    onOpen?: (accordionSlug: string) => void
    equipments: SingleEquipment[] | SinglePromotionEquipment[]
    estimate_cta_url: string | null
    modelName: string
    modelSlug: string
    isDealerSelected: boolean
    isPromoAvailable: number
    dealerName: string
    promoScope: string
}

// tslint:disable-next-line:cyclomatic-complexity
export default function AccordionItem(props: AccordionItem): React.JSX.Element {
    const {t} = useTranslation('app')
    // tslint:disable-next-line:no-unnecessary-type-assertion
    const dispatch = useDispatch() as AppDispatch
    const {tracking: trackingManager} = useFeatureServices()
    const {'layer-manager': layerManager} = useFeatureServices() as {
        'layer-manager': LayerManagerV2
    }
    const trackingBrand = useSelector(
        (state: RootState) => state.general.trackingBrand
    )
    const isShowroom = useSelector(
        (state: RootState) => state.general.isShowroom
    )
    const showRFQForm = useSelector(
        (state: RootState) => state.general.showRFQForm
    )
    const printAccordionItem = isExpandablePromotion(props)
    const printLegalNotesCta = hasLegalNotes(props.tooltip_text)
    const isEstimateCta = !showRFQForm && props.estimate_cta_url
    const isRfqCta = showRFQForm && isShowroom
    const printEstimateCTA =
        (showRFQForm && isShowroom) || (!showRFQForm && props.estimate_cta_url)

    const setActiveHandler = (key: number, clickFromUser = true): void => {
        props.onClick(key, clickFromUser)
    }
    const legalNotesCtaText = t('cta.singlePromotion.legalNotes')
    const motorizationExists = props.motorization && props.motorization.length
    const subTitleExists = props.sub_title && props.sub_title.length

    const PromotionalPackageText: AdditionalPromoText = {
        additional_promotion_title: props.additional_promotion_title,
        additional_promotion_detail_1: props.additional_promotion_detail_1,
        additional_promotion_detail_2: props.additional_promotion_detail_2,
        additional_promotion_detail_3: props.additional_promotion_detail_3,
        is_additional_promotion_detail_visible_1:
            props.is_additional_promotion_detail_visible_1,
        is_additional_promotion_detail_visible_2:
            props.is_additional_promotion_detail_visible_2,
        is_additional_promotion_detail_visible_3:
            props.is_additional_promotion_detail_visible_3,
    }

    const handleAccordionClick: AccordionProps['onClick'] = (e) => {
        if (!printAccordionItem) {
            return
        }

        const ANCHOR_TAG_NAME = 'A'
        const target = e?.target as HTMLElement

        if (target.tagName === ANCHOR_TAG_NAME) {
            return
        }

        if (props.id === props.openIndex) {
            if (checkTrackingManager(trackingManager)) {
                setTracking(
                    trackingManager,
                    TrackingAction.VWBasic_VehiclePromotionsAccordionClose_Click,
                    getAccordionCloseClickData(
                        props.modelName,
                        props.modelSlug,
                        props.title,
                        props.motorization,
                        trackingBrand,
                        isShowroom
                    )
                )
            }

            setActiveHandler(
                AccordionSettings.ACCORDION_INDEX_CLOSE_ALL_ITEMS,
                true
            )
            if (props.onClose) {
                props.onClose()
            }

            return
        }

        if (checkTrackingManager(trackingManager)) {
            setTracking(
                trackingManager,
                TrackingAction.VWBasic_VehiclePromotionsAccordionOpen_Click,
                getAccordionOpenClickData(
                    props.modelName,
                    props.modelSlug,
                    props.title,
                    props.motorization,
                    trackingBrand,
                    isShowroom
                )
            )
        }

        if (props.onOpen) {
            props.onOpen(props.slug)
        }
        setActiveHandler(props.id, true)
    }

    useEffect(() => {
        if (props.isOpen) {
            setActiveHandler(props.id, false)
        }
    }, [])

    const simpleItemCta =
        (props.cta_text && props.cta_url) ||
        (props.is_promo_service && props.customizable_cta_text) ? (
            <SimpleItemCTA>
                <CTA
                    tag="a"
                    emphasis="tertiary"
                    href={props.cta_url}
                    aria-label={getCtaLabel(
                        props.cta_text,
                        props.title,
                        props.is_promo_service,
                        props.customizable_cta_text
                    )}
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()

                        if (checkTrackingManager(trackingManager)) {
                            setTracking(
                                trackingManager,
                                TrackingAction.VWBasic_VehiclePromotionsLink_Click,
                                getVehiclePromotionsLinkClickData(
                                    getCtaLabel(
                                        props.cta_text,
                                        props.title,
                                        props.is_promo_service,
                                        props.customizable_cta_text
                                    ),
                                    props.title,
                                    props.modelName,
                                    props.modelSlug,
                                    trackingBrand,
                                    isShowroom
                                )
                            )
                        }
                    }}
                >
                    <span
                        dangerouslySetInnerHTML={{
                            __html: getCtaLabel(
                                props.cta_text,
                                props.title,
                                props.is_promo_service,
                                props.customizable_cta_text
                            ),
                        }}
                    />

                    <ArrowRight aria-hidden={true} />
                </CTA>
            </SimpleItemCTA>
        ) : undefined

    const legalNotesCta =
        props.tooltip_text && printLegalNotesCta ? (
            <CTAContainer>
                <LegalNotesCta
                    tag="button"
                    emphasis="tertiary"
                    aria-label={legalNotesCtaText}
                    onClick={(e: React.MouseEvent): void => {
                        e.preventDefault()
                        e.stopPropagation()

                        legalNotesLayerHandle(layerManager, () => {
                            dispatch(setIsLegalNotesOpen(false))
                            dispatch(setLegalNotesText(''))
                        })
                        dispatch(setIsLegalNotesOpen(true))
                        dispatch(setLegalNotesText(props.tooltip_text))

                        if (checkTrackingManager(trackingManager)) {
                            setTracking(
                                trackingManager,
                                TrackingAction.VWBasic_VehiclePromotionsLink_Click,
                                getVehiclePromotionsLinkClickData(
                                    legalNotesCtaText,
                                    props.title,
                                    props.modelName,
                                    props.modelSlug,
                                    trackingBrand,
                                    isShowroom
                                )
                            )
                        }
                    }}
                >
                    <Info aria-hidden={true} />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: legalNotesCtaText,
                        }}
                    />
                </LegalNotesCta>
            </CTAContainer>
        ) : undefined

    const estimateCta =
        printAccordionItem && printEstimateCTA ? (
            <EstimateCtaContainer>
                <CTA
                    tag={isEstimateCta ? 'a' : 'button'}
                    {...(isEstimateCta && props.estimate_cta_url
                        ? {href: props.estimate_cta_url}
                        : {})}
                    emphasis="primary"
                    aria-label={t('cta.singlePromotion.requestQuote')}
                    onClick={(e: React.MouseEvent): void => {
                        e.stopPropagation()
                        dispatch(
                            setSelectedPromotion({
                                id: props.id,
                                title: props.title,
                                motorization: props.motorization,
                            })
                        )
                        if (isRfqCta) {
                            dispatch(
                                setFormCycleID(
                                    generateRandomString(Global.CYCLE_ID_LENGTH)
                                )
                            )
                            openCouponLayer()
                        }
                        const promotionsClickData =
                            getVehiclePromotionsButtonClickData(
                                t('cta.singlePromotion.requestQuote'),
                                props.modelName,
                                props.modelSlug,
                                trackingBrand,
                                isShowroom,
                                props.title
                            )
                        if (checkTrackingManager(trackingManager)) {
                            setTracking(
                                trackingManager,
                                TrackingAction.VWBasic_VehiclePromotionsButton_Click,
                                promotionsClickData
                            )
                        }
                    }}
                >
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t('cta.singlePromotion.requestQuote'),
                        }}
                    />
                </CTA>
            </EstimateCtaContainer>
        ) : undefined

    const headline = (
        <div>
            {props.is_promo_service ? undefined : (
                <HeadlineFuelTags fuels={props.fuels} />
            )}

            {motorizationExists ? (
                <HeadlineSubTitle
                    tag={TextTag.p}
                    color={TokenTextColor.primary}
                >
                    {props.motorization}
                </HeadlineSubTitle>
            ) : undefined}

            {subTitleExists && !motorizationExists ? (
                <HeadlineSubTitle
                    tag={TextTag.p}
                    color={TokenTextColor.primary}
                >
                    {props.sub_title}
                </HeadlineSubTitle>
            ) : undefined}

            <HeadlineTitle
                tag={TextTag.h2}
                appearance={TokenTextAppearance.headline300}
                bold={!(props.is_promo_service && props.customizable_title)}
            >
                <HeadlineTitleText
                    dangerouslySetInnerHTML={{
                        __html: getPromoTitle(
                            props.title,
                            props.is_promo_service,
                            props.customizable_title
                        ),
                    }}
                />

                {hasPromotionPrice(props) ? (
                    <HeadlinePriceContainer tag={TextTag.span}>
                        <PromotionPrice
                            price={props.price}
                            price_prefix={props.price_prefix}
                            price_suffix={props.price_suffix}
                        />
                    </HeadlinePriceContainer>
                ) : undefined}
            </HeadlineTitle>

            <SubTitleContainer>
                {props.price_subtext ? (
                    <AccordionSubText
                        tag={TextTag.p}
                        appearance={TokenTextAppearance.copy100}
                    >
                        {props.price_subtext}
                    </AccordionSubText>
                ) : undefined}
            </SubTitleContainer>

            {props.promotional_package ? (
                <EyeCatcherAccordionContainer>
                    <EyeCatcher
                        backgroundColor={ULTRAMARINE_EYE_CATCHER_BACKGROUND}
                        contentColor={globalColors.white000}
                        text={props.promotional_package}
                    />
                </EyeCatcherAccordionContainer>
            ) : undefined}

            {props.isDealerSelected &&
                props.promoScope.toString() === PromotionType.NATIONAL &&
                props.isPromoAvailable === 0 && (
                    <EyeCatcherAccordionContainer className="promo-not-available-container">
                        <EyeCatcher
                            text={`${t(
                                'selectedDealer.promoNotAvailable'
                            )} <b>${capitalizeWords(props.dealerName)}</b>`}
                            backgroundColor={PURPLE_EYE_CATCHER_BACKGROUND}
                        />
                    </EyeCatcherAccordionContainer>
                )}
        </div>
    )

    return (
        <AccordionItemWrapper
            id={props.slug}
            removeArrow={!printAccordionItem}
            className={` ${
                props.isDealerSelected &&
                props.promoScope === PromotionType.LOCAL
                    ? PROMO_AVAILABLE_CLASS
                    : ''
            }`}
        >
            {props.isDealerSelected &&
                props.promoScope === PromotionType.LOCAL && (
                    <DealerHighlighter
                        dealerName={capitalizeWords(props.dealerName)}
                        alignedLeft={true}
                    />
                )}

            {printAccordionItem ? (
                <Accordion
                    headline={headline}
                    id={props.id.toString()}
                    key={props.id}
                    isOpen={props.isOpen || props.id === props.openIndex}
                    onClick={handleAccordionClick}
                >
                    <AccordionInner>
                        {simpleItemCta}
                        {legalNotesCta}
                        {estimateCta}

                        {props.description ? (
                            <Text appearance={TokenTextAppearance.copy200}>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: props.description,
                                    }}
                                />
                            </Text>
                        ) : undefined}

                        {typeof props.equipments !== 'undefined' &&
                        props.equipments.length ? (
                            <StandardEquipmentContainer>
                                <StandardEquipment
                                    hasImage={props.has_image}
                                    image={props.custom_image}
                                    imageAlt={props.title}
                                    equipment={props.equipments}
                                />
                            </StandardEquipmentContainer>
                        ) : undefined}

                        {props.has_additional_promotion ? (
                            <PromotionalPackageContainer>
                                <PromotionalPackage
                                    promotionText={PromotionalPackageText}
                                />
                            </PromotionalPackageContainer>
                        ) : undefined}
                    </AccordionInner>
                </Accordion>
            ) : (
                <FakeAccordion
                    onClick={() => {
                        if (props.id === props.openIndex) {
                            if (checkTrackingManager(trackingManager)) {
                                setTracking(
                                    trackingManager,
                                    TrackingAction.VWBasic_VehiclePromotionsAccordionClose_Click,
                                    getAccordionCloseClickData(
                                        props.modelName,
                                        props.modelSlug,
                                        props.title,
                                        props.motorization,
                                        trackingBrand,
                                        isShowroom
                                    )
                                )
                            }

                            setActiveHandler(
                                AccordionSettings.ACCORDION_INDEX_CLOSE_ALL_ITEMS,
                                true
                            )
                            if (props.onClose) {
                                props.onClose()
                            }

                            return
                        }

                        if (checkTrackingManager(trackingManager)) {
                            setTracking(
                                trackingManager,
                                TrackingAction.VWBasic_VehiclePromotionsAccordionOpen_Click,
                                getAccordionOpenClickData(
                                    props.modelName,
                                    props.modelSlug,
                                    props.title,
                                    props.motorization,
                                    trackingBrand,
                                    isShowroom
                                )
                            )
                        }

                        if (props.onOpen) {
                            props.onOpen(props.slug)
                        }
                        setActiveHandler(props.id, true)
                    }}
                >
                    {headline}
                    {simpleItemCta}
                    {legalNotesCta}
                    {estimateCta}
                </FakeAccordion>
            )}
        </AccordionItemWrapper>
    )
}
